/**
 * Hand-drawn botanical ornaments.
 *
 * Every shape is stroke-based with deliberately uneven curves — nothing here
 * is a perfect circle or a mirrored path, because symmetry is what makes
 * decoration read as clip-art. Add new ornaments to this file and they
 * inherit the same visual language for free.
 */
import type { SVGProps } from 'react'

type Ornament = SVGProps<SVGSVGElement> & { tone?: string }

/** A single stem with alternating leaves. The workhorse ornament. */
export function Sprig({ tone = 'currentColor', ...props }: Ornament) {
  return (
    <svg viewBox="0 0 60 150" fill="none" aria-hidden="true" {...props}>
      <g stroke={tone} strokeWidth="1.3" strokeLinecap="round">
        <path d="M30 148C30 148 27 108 29 82C30.6 61 36 34 33 4" />
        <path d="M29 118C22 116 13 110 10 99c9-1 17 3 20 12Z" fill={tone} fillOpacity="0.1" />
        <path d="M30 100c7-3 15-10 16-21-9 1-16 6-17 15Z" fill={tone} fillOpacity="0.14" />
        <path d="M30 80c-8-2-17-8-20-19 9-1 18 4 21 14Z" fill={tone} fillOpacity="0.1" />
        <path d="M32 58c7-4 13-12 12-23-8 3-13 10-13 19Z" fill={tone} fillOpacity="0.16" />
        <path d="M33 36c-7-3-13-10-14-20 8 2 14 9 15 18Z" fill={tone} fillOpacity="0.1" />
      </g>
    </svg>
  )
}

/** A long, lazy vine — meant to run down the margin of a page. */
export function Vine({ tone = 'currentColor', ...props }: Ornament) {
  return (
    <svg viewBox="0 0 120 460" fill="none" aria-hidden="true" {...props}>
      <g stroke={tone} strokeWidth="1.2" strokeLinecap="round">
        <path d="M62 2C62 2 40 58 48 108s34 62 22 112-38 60-24 108 40 56 34 128" />
        <path d="M50 74c-11-4-22-15-24-30 13 0 24 10 26 24Z" fill={tone} fillOpacity="0.12" />
        <path d="M52 120c12-2 24-11 27-26-13-2-25 6-29 20Z" fill={tone} fillOpacity="0.09" />
        <path d="M66 176c-12-3-23-13-26-28 13-1 25 8 28 22Z" fill={tone} fillOpacity="0.13" />
        <path d="M62 236c12 0 26-7 31-22-13-3-27 4-32 18Z" fill={tone} fillOpacity="0.09" />
        <path d="M44 300c-12-4-22-15-24-30 13 1 24 11 26 25Z" fill={tone} fillOpacity="0.12" />
        <path d="M48 356c13-1 26-9 30-24-13-2-26 6-31 20Z" fill={tone} fillOpacity="0.09" />
        <path d="M66 412c-11-4-21-14-23-29 13 1 23 11 25 25Z" fill={tone} fillOpacity="0.12" />
      </g>
    </svg>
  )
}

/** A horizontal ornament for breaking sections without a hard line. */
export function LeafDivider({ tone = 'currentColor', ...props }: Ornament) {
  return (
    <svg viewBox="0 0 220 26" fill="none" aria-hidden="true" {...props}>
      <g stroke={tone} strokeWidth="1.2" strokeLinecap="round">
        <path d="M2 14h72" opacity="0.45" />
        <path d="M146 14h72" opacity="0.45" />
        <path d="M110 24c-7-4-12-11-12-18 7 2 12 9 12 18Z" fill={tone} fillOpacity="0.16" />
        <path d="M110 24c7-4 12-11 12-18-7 2-12 9-12 18Z" fill={tone} fillOpacity="0.1" />
        <path d="M110 24V6" />
        <circle cx="86" cy="14" r="1.9" fill={tone} fillOpacity="0.5" stroke="none" />
        <circle cx="134" cy="14" r="1.9" fill={tone} fillOpacity="0.5" stroke="none" />
      </g>
    </svg>
  )
}

/** Two seed leaves pushing out of the ground. Used on empty states. */
export function Seedling({ tone = 'currentColor', ...props }: Ornament) {
  return (
    <svg viewBox="0 0 80 70" fill="none" aria-hidden="true" {...props}>
      <g stroke={tone} strokeWidth="1.4" strokeLinecap="round">
        <path d="M40 66V30" />
        <path d="M40 38c-13-1-24-9-27-23 14-2 26 8 27 23Z" fill={tone} fillOpacity="0.13" />
        <path d="M40 33c12-3 21-12 22-25-13 1-22 11-22 25Z" fill={tone} fillOpacity="0.09" />
        <path d="M22 66h36" opacity="0.35" strokeDasharray="1 5" />
      </g>
    </svg>
  )
}

/** A loose open flower. Deliberately lopsided. */
export function Bloom({ tone = 'currentColor', ...props }: Ornament) {
  return (
    <svg viewBox="0 0 70 70" fill="none" aria-hidden="true" {...props}>
      <g stroke={tone} strokeWidth="1.25" strokeLinecap="round">
        <path d="M35 34c-4-9-2-18 5-24 5 8 4 17-5 24Z" fill={tone} fillOpacity="0.13" />
        <path d="M35 34c9-3 18 0 23 7-8 4-17 2-23-7Z" fill={tone} fillOpacity="0.1" />
        <path d="M35 34c5 8 4 18-3 24-5-8-4-17 3-24Z" fill={tone} fillOpacity="0.14" />
        <path d="M35 34c-9 4-18 2-24-5 8-5 17-4 24 5Z" fill={tone} fillOpacity="0.09" />
        <circle cx="35" cy="34" r="3.6" fill={tone} fillOpacity="0.32" />
      </g>
    </svg>
  )
}

/**
 * Decorative botanicals anchored to the page edges. Purely atmospheric —
 * pointer-events off, hidden from assistive tech, hidden on small screens
 * where there is no margin to spare.
 */
export function PageBotanicals() {
  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
      <Vine
        tone="var(--c-sage)"
        className="absolute -left-6 top-10 h-[70vh] w-auto opacity-[0.32] animate-sway"
        style={{ transformOrigin: 'top center' }}
      />
      <Vine
        tone="var(--c-clay)"
        className="absolute -right-8 bottom-0 h-[60vh] w-auto opacity-[0.26] scale-x-[-1] animate-sway"
        style={{ transformOrigin: 'bottom center', animationDelay: '-4s' }}
      />
      <Bloom tone="var(--c-terracotta)" className="absolute right-[12%] top-[16%] h-16 w-16 opacity-25 animate-drift" />
      <Sprig tone="var(--c-moss)" className="absolute left-[8%] bottom-[8%] h-32 w-auto opacity-20 animate-drift" style={{ animationDelay: '-5s' }} />
    </div>
  )
}
